import React, { useState, useCallback } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { HiX, HiPlus, HiLink } from "react-icons/hi";
import { ActivityLog, TrainingLog } from "../../types/post";
import { useAuth } from "../../contexts/AuthContext";
import { linkActivityToTraining } from "../../services/dayPostService";
import TrainingLogForm from "../DayPost/training-log/TrainingLogForm";

interface ActivityLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  activity: ActivityLog & { id: string };
  unlinkedTrainingLogs: (TrainingLog & { id: string })[];
  dateID: string;
  onSuccess: () => void;
}

const ActivityLinkModal: React.FC<ActivityLinkModalProps> = ({
  isOpen,
  onClose,
  activity,
  unlinkedTrainingLogs,
  dateID,
  onSuccess,
}) => {
  const { currentUser } = useAuth();
  const [isLinking, setIsLinking] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const handleLinkExisting = useCallback(
    async (trainingLogID: string) => {
      if (!currentUser?.uid) return;

      try {
        setIsLinking(true);
        await linkActivityToTraining(
          currentUser.uid,
          activity.id,
          trainingLogID
        );
        onSuccess();
        onClose();
      } catch (error) {
        console.error("Error linking activity to training:", error);
      } finally {
        setIsLinking(false);
      }
    },
    [currentUser?.uid, activity.id, onSuccess, onClose]
  );

  const handleCreateNew = useCallback(() => {
    setShowCreateForm(true);
  }, []);

  const handleCloseCreateForm = useCallback(() => {
    setShowCreateForm(false);
  }, []);

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };
const formatActivityDate = (dateID: string): string => {
  const year = parseInt(dateID.substring(0, 4));
  const month = parseInt(dateID.substring(4, 6)) - 1;
  const day = parseInt(dateID.substring(6, 8));
  return new Date(year, month, day).toLocaleDateString("en-US", {
    weekday: "short", month: "long", day: "numeric"
  });
};
  if (showCreateForm) {
    return (
      <TrainingLogForm
        open={true}
        onClose={handleCloseCreateForm}
        context={{ dateID, activityLogId: activity.id }}
        initialData={{
          muscular: 1,
          respiratory: 1,
          notes: `${activity.sessionName || "Activity"}`,
          duration: Math.round((activity.duration || 0) * 100) / 100,
          trainingPerformed: 1,
        }}
        onSaved={() => {
          onSuccess();
          onClose();
        }}
      />
    );
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="p-2 w-full max-w-md transform overflow-hidden rounded-lg bg-base-100 text-left align-middle shadow-xl transition-all">
                <div className="flex justify-between items-center p-6 border-b border-base-200">
                  <div>
                    <Dialog.Title className="text-xl font-bold font-grotesk text-base-content">
                      Link Activity
                    </Dialog.Title>
                    {/* <p className="text-sm text-base-content/80 mt-1">
                      {activity.sessionName || "Activity"} •{" "}
                      {formatDuration(activity.duration || 0)}
                    </p> */}
                    <p className="text-sm text-base-content/80 mt-1">
                      {activity.sessionName || "Activity"} •{" "}
                      {formatDuration(activity.duration || 0)} •{" "}
                      <span className="font-medium">{formatActivityDate(dateID)}</span>
                    </p>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-base-200 rounded-full transition-colors"
                  >
                    <HiX className="w-5 h-5 text-base-content/60" />
                  </button>
                </div>

                <div className="p-6 space-y-4">
                  {/* Create New Training Log */}
                  <button
                    onClick={handleCreateNew}
                    disabled={isLinking}
                    className="w-full flex items-center justify-center space-x-3 p-4 border-2 border-dashed border-base-300 rounded-lg hover:border-primary hover:bg-primary hover:bg-opacity-5 transition-colors group"
                  >
                    <HiPlus className="w-5 h-5 text-base-content/50 group-hover:text-primary" />
                    <span className="text-base-content/80 group-hover:text-primary font-medium">
                      Create New Training Log
                    </span>
                  </button>

                  {/* Link to Existing Training Log */}
                  {unlinkedTrainingLogs.length > 0 && (
                    <>
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-base-300" />
                        </div>
                        <div className="relative flex justify-center text-sm">
                          <span className="px-2 bg-base-100 text-base-content/60">
                            or link to existing
                          </span>
                        </div>
                      </div>

                      <div className="space-y-4 max-h-60 overflow-y-auto">
                        {unlinkedTrainingLogs.map((trainingLog) => (
                          <button
                            key={trainingLog.id}
                            onClick={() => handleLinkExisting(trainingLog.id)}
                            disabled={isLinking}
                            className="w-full flex items-center justify-between p-3 border border-base-300 bg-base-200 rounded-lg hover:border-primary hover:bg-primary hover:bg-opacity-5 transition-colors group"
                          >
                            <div className="flex items-center space-x-3">
                              <HiLink className="w-4 h-4 text-base-content/50 group-hover:text-primary" />
                              <div className="text-left">
                                <p className="text-sm font-medium text-base-content">
                                  {trainingLog.notes || "Training Session"}
                                </p>
                                <p className="text-xs text-base-content/60">
                                  {formatDuration(trainingLog.duration)} •{""}
                                  <strong>{trainingLog.load}</strong> load
                                </p>
                              </div>
                            </div>
                            {isLinking && (
                              <div className="loading loading-spinner loading-sm"></div>
                            )}
                          </button>
                        ))}
                      </div>
                    </>
                  )}

                  {unlinkedTrainingLogs.length === 0 && (
                    <div className="text-center py-4">
                      <p className="text-sm text-base-content/60">
                        No unlinked training sessions available
                      </p>
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ActivityLinkModal;
